import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from 'app/core/services/auth.service';
import { WorkingLaboratoryService } from 'app/core/services/prothesis-api/working-laboratory.service';
import { WorkingCustomerService } from 'app/core/services/prothesis-customer-api/working-customer.service';
import { CustomerTenantPickerComponent } from 'app/shared/components/pickers/customer-tenant-picker/customer-tenant-picker.component';
import { CustomerTenantModel } from 'app/shared/components/pickers/customer-tenant-picker/models/customer-tenant-model';
import { LaboratoryTenantPickerComponent } from 'app/shared/components/pickers/laboratory-tenant-picker/laboratory-tenant-picker.component';
import { LaboratoryTenantModel } from 'app/shared/components/pickers/laboratory-tenant-picker/models/laboratory-tenant-model';
import { Area } from 'app/shared/enums/area.enum';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TenantSelectorFormGroup } from './form-groups/tenant-selector-form-group';
import { TenantSelectorModelHelper } from './model-helpers/tenant-selector.model-helpers';
import { TenantSelectorModel } from './models/tenant-selector.model';
import { MatIcon } from '@angular/material/icon';
import { GroupedLabelCardComponent } from '../../../../shared/components/cards/grouped-label-card/grouped-label-card.component';
import { CustomerTenantPickerComponent as CustomerTenantPickerComponent_1 } from '../../../../shared/components/pickers/customer-tenant-picker/customer-tenant-picker.component';
import { LaboratoryTenantPickerComponent as LaboratoryTenantPickerComponent_1 } from '../../../../shared/components/pickers/laboratory-tenant-picker/laboratory-tenant-picker.component';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-tenant-selector',
  styleUrls: ['./tenant-selector.component.scss'],
  templateUrl: './tenant-selector.component.html',
  imports: [
    FormsModule,
    ReactiveFormsModule,

    MatIcon,
    GroupedLabelCardComponent,
    CustomerTenantPickerComponent_1,
    LaboratoryTenantPickerComponent_1,
  ],
})
export class TenantSelectorComponent implements OnInit, OnDestroy {
  @ViewChild('_customerTenantPickerComponent') private _customerTenantPickerComponent: CustomerTenantPickerComponent;
  @ViewChild('_laboratoryTenantPickerComponent') private _laboratoryTenantPickerComponent: LaboratoryTenantPickerComponent;
  public allowedCustomerUuids: string[];
  public allowedLaboratoryUuids: string[];
  public formGroup: FormGroup<TenantSelectorFormGroup>;
  public tenantSelector: TenantSelectorModel;

  private _authService = inject(AuthService);
  private _changeDetectorRef = inject(ChangeDetectorRef);
  private _router = inject(Router);
  private _unsubscribeAll$: Subject<void> = new Subject();
  private _workingCustomerService = inject(WorkingCustomerService);
  private _workingLaboratoryService = inject(WorkingLaboratoryService);

  public customerTenantClickHandler(): boolean {
    this._customerTenantPickerComponent._pickerComponent.openPickerDialog();
    return false;
  }

  public customerTenantPickerAddedHandler(customerTenant: CustomerTenantModel): boolean {
    this._workingLaboratoryService.clear();
    this._workingCustomerService.clear();
    this._authService.configureCustomerArea(customerTenant.uuid).then(() => {
      this._router.navigateByUrl('');
    });
    return true;
  }

  public laboratoryTenantClickHandler(): boolean {
    this._laboratoryTenantPickerComponent._pickerComponent.openPickerDialog();
    return false;
  }

  public laboratoryTenantPickerAddedHandler(laboratoryTenant: LaboratoryTenantModel): boolean {
    this._workingLaboratoryService.clear();
    this._workingCustomerService.clear();
    this._authService.configureLaboratoryArea(laboratoryTenant.uuid).then(() => {
      this._router.navigateByUrl('');
    });
    return true;
  }

  public ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll$.next();
    this._unsubscribeAll$.complete();
  }

  public ngOnInit(): void {
    const tenantSelector = TenantSelectorModelHelper.build();

    this.formGroup = new FormGroup<TenantSelectorFormGroup>({
      customerTenant: new FormControl<CustomerTenantModel>(tenantSelector.customerTenant),
      displayCustomerTenantPickerFlag: new FormControl<boolean>(tenantSelector.displayCustomerTenantPickerFlag),
      displayLaboratoryTenantPickerFlag: new FormControl<boolean>(tenantSelector.displayLaboratoryTenantPickerFlag),
      laboratoryTenant: new FormControl<LaboratoryTenantModel>(tenantSelector.laboratoryTenant),
    });

    this._authService.isDoneLoading$.pipe(takeUntil(this._unsubscribeAll$)).subscribe((isDoneLoadingFlag) => {
      if (isDoneLoadingFlag) {
        this.allowedCustomerUuids = this._authService.allowedCustomerUuids;
        this.allowedLaboratoryUuids = this._authService.allowedLaboratoryUuids;
        this._changeDetectorRef.markForCheck();
      }
    });

    this._authService.areaConfiguredFlag$.pipe(takeUntil(this._unsubscribeAll$)).subscribe(() => {
      if (this._authService.area === Area.Customer) {
        this._updateCustomerTenant();
      } else if (this._authService.area === Area.Laboratory) {
        this._updateLaboratoryTenant();
      } else {
        this.formGroup.controls.laboratoryTenant.setValue(undefined!);
        this.formGroup.controls.customerTenant.setValue(undefined!);
        this.formGroup.controls.displayCustomerTenantPickerFlag.setValue(false);
        this.formGroup.controls.displayLaboratoryTenantPickerFlag.setValue(false);
      }
      this._changeDetectorRef.markForCheck();
    });
  }

  public pickerDialogAfterClosedEventHandler(): void {
    if (this.formGroup.controls.displayCustomerTenantPickerFlag?.value) {
      this._customerTenantPickerComponent?.setFocus();
    } else {
      this._laboratoryTenantPickerComponent?.setFocus();
    }
  }

  private _updateCustomerTenant(): void {
    const customerTenant = new CustomerTenantModel();
    customerTenant.customerGroupFlag = this._workingCustomerService?.customerGroupFlag;
    customerTenant.customerGroupName = this._workingCustomerService?.customerMemberFlag
      ? this._workingCustomerService?.customerGroupName
      : null;
    customerTenant.laboratoryName = this._workingLaboratoryService?.laboratoryName;
    customerTenant.name = this._workingCustomerService?.customerName;
    customerTenant.uuid = this._workingCustomerService?.tenantUuid;
    this.formGroup.controls.customerTenant.setValue(customerTenant);
    this.formGroup.controls.displayCustomerTenantPickerFlag.setValue(true);
    this.formGroup.controls.displayLaboratoryTenantPickerFlag.setValue(false);
  }

  private _updateLaboratoryTenant(): void {
    const laboratoryTenant = new LaboratoryTenantModel();
    laboratoryTenant.name = this._workingLaboratoryService?.laboratoryName;
    laboratoryTenant.uuid = this._workingLaboratoryService?.tenantUuid;
    this.formGroup.controls.laboratoryTenant.setValue(laboratoryTenant);
    this.formGroup.controls.displayCustomerTenantPickerFlag.setValue(false);
    this.formGroup.controls.displayLaboratoryTenantPickerFlag.setValue(true);
  }
}
